import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { api, errMsg, eur, CATEGORIES, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ExcelButton, PageHeader, Spinner } from "@/components/common";
import Modal from "@/components/Modal";

function Gauge({ b, onEdit, s }) {
  const cons = Math.min(100, b.consumed_pct);
  const eng = Math.min(100 - cons, b.pct - b.consumed_pct);
  const color = b.pct >= s.budget_alert_2 ? "#B03A3A" : b.pct >= s.budget_alert_1 ? "#A5894E" : "#16163F";
  return (
    <div className="space-y-1.5" data-testid={`budget-gauge-${b.cost_center}-${b.category}`}>
      <div className="flex items-center justify-between text-sm">
        <span className="font-semibold">{CATEGORIES[b.category]}</span>
        <span className="flex items-center gap-2">
          <span className="num font-bold" style={{ color }}>{String(b.pct).replace(".", ",")} %</span>
          {b.pct >= s.budget_alert_1 && <span className="rounded px-1.5 text-[10px] font-bold text-white" style={{ background: color }}>{b.pct >= s.budget_alert_2 ? "Dépassé" : `≥ ${s.budget_alert_1} %`}</span>}
          {onEdit && <button className="btn-ghost !p-1" onClick={onEdit} data-testid={`budget-edit-${b.cost_center}-${b.category}`}><Pencil size={13} /></button>}
        </span>
      </div>
      <div className="flex h-2.5 overflow-hidden rounded-full bg-[#E4E4EC]">
        <div style={{ width: `${cons}%`, background: color }} className="transition-[width] duration-700" />
        <div style={{ width: `${Math.max(0, eng)}%` }} className="bg-[#C9B07F]" />
      </div>
      <div className="num flex justify-between text-[11px] text-[#6B6B80]">
        <span>Consommé {eur(b.consumed)} · Engagé {eur(b.engaged)}</span><span>Disponible <b className="text-[#16163F]">{eur(b.remaining)}</b> / {eur(b.amount)}</span>
      </div>
    </div>
  );
}

export default function Budgets() {
  const { user, settings } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/budgets", { params: { year } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [year]); // eslint-disable-line
  if (!rows || !settings) return <Spinner />;
  const save = async (e) => {
    e.preventDefault();
    if (Number(edit.amount) < 0) return toast.error("Le montant doit être positif");
    try { await api.post("/budgets", { ...edit, amount: Number(edit.amount), year }); toast.success("Budget enregistré"); setEdit(null); load(); } catch (x) { toast.error(errMsg(x)); }
  };
  const cols = [{ key: "cost_center", label: "Centre de coût" }, { key: "category", label: "Catégorie", value: (r) => CATEGORIES[r.category] }, { key: "amount", label: "Budget", type: "money" },
    { key: "engaged", label: "Engagé", type: "money" }, { key: "consumed", label: "Consommé", type: "money" }, { key: "remaining", label: "Disponible", type: "money" }, { key: "pct", label: "Utilisation %", type: "pct" }];
  return (
    <div>
      <PageHeader title="Budgets" subtitle="Engagé = commandes non facturées · Consommé = factures validées, payées ou comptabilisées (HT).">
        <select className="inp !w-28" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="budget-year-select">
          {[1, 0, -1, -2].map((d) => <option key={d} value={new Date().getFullYear() + d}>{new Date().getFullYear() + d}</option>)}
        </select>
        <ExcelButton title={`Budgets ${year}`} columns={cols} rows={rows} testid="budgets-export-btn" />
        {can(user, "admin") && <button className="btn-primary" onClick={() => setEdit({ cost_center: settings.cost_centers[0].code, category: "materiel", amount: 0 })} data-testid="new-budget-btn">Définir un budget</button>}
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-2">
        {settings.my_cost_centers.map((cc, i) => {
          const list = rows.filter((b) => b.cost_center === cc.code);
          const tot = list.reduce((a, b) => ({ amount: a.amount + b.amount, used: a.used + b.consumed + b.engaged }), { amount: 0, used: 0 });
          return (
            <div key={cc.code} className="card rise space-y-5 p-6" style={{ animationDelay: `${i * 60}ms` }} data-testid={`budget-card-${cc.code}`}>
              <div className="flex items-baseline justify-between">
                <div><h2 className="text-lg font-bold">{cc.label}</h2><p className="mono text-xs text-[#6B6B80]">{cc.code}</p></div>
                <p className="num text-right text-sm"><span className="text-2xl font-extrabold">{eur(tot.amount)}</span><span className="block text-xs text-[#6B6B80]">utilisé {tot.amount ? Math.round((tot.used / tot.amount) * 100) : 0} %</span></p>
              </div>
              {list.map((b) => <Gauge key={b.category} b={b} s={settings} onEdit={can(user, "admin") ? () => setEdit({ cost_center: b.cost_center, category: b.category, amount: b.amount }) : null} />)}
              {!list.length && <p className="text-sm text-[#6B6B80]">Aucun budget défini.</p>}
            </div>
          );
        })}
      </div>
      <Modal open={!!edit} onClose={() => setEdit(null)} title={`Budget ${year}`} testid="budget-modal">
        {edit && <form onSubmit={save} className="space-y-4">
          <select className="inp" value={edit.cost_center} onChange={(e) => setEdit({ ...edit, cost_center: e.target.value })} data-testid="budget-cc-select">{settings.cost_centers.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}</select>
          <select className="inp" value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} data-testid="budget-cat-select">{Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          <input type="number" min="0" step="100" className="inp" value={edit.amount} onChange={(e) => setEdit({ ...edit, amount: e.target.value })} data-testid="budget-amount-input" />
          <div className="flex justify-end"><button className="btn-primary" data-testid="budget-save-btn">Enregistrer</button></div>
        </form>}
      </Modal>
    </div>
  );
}
